import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { handleError } from '../utils/utils';

import type {
    CmsUserSummary,
    RoleCatalogBlock,
} from '../types/userManagementTypes';

const DEFAULT_BASEPATH = '/api';
const USERS_QUERY_KEY = 'cms-users';
const ROLE_CATALOG_QUERY_KEY = 'cms-role-catalog';

const getBasepath = (basepath?: string) => basepath ?? DEFAULT_BASEPATH;

export function useUserRoleCatalog(options: { basepath?: string } = {}) {
    const basepath = getBasepath(options.basepath);

    const query = useQuery({
        queryKey: [ROLE_CATALOG_QUERY_KEY, basepath],
        staleTime: Infinity,
        queryFn: async () => {
            const { data } = await axios.get<{ blocks: RoleCatalogBlock[] }>(
                `${basepath}/v2/users/role-catalog`,
            );
            return data.blocks;
        },
    });

    return {
        blocks: query.data ?? [],
        isLoading: query.isLoading,
        errorMessage: query.error
            ? handleError('Fetch role catalog error', query.error, false)
            : undefined,
    };
}

export function useCmsUserSearch(
    query: string,
    options: { basepath?: string } = {},
) {
    const basepath = getBasepath(options.basepath);
    const trimmed = query.trim();

    const result = useQuery({
        queryKey: [USERS_QUERY_KEY, basepath, 'search', trimmed],
        enabled: trimmed.length > 0,
        queryFn: async () => {
            const { data } = await axios.get<{ users: CmsUserSummary[] }>(
                `${basepath}/v2/users`,
                { params: { query: trimmed, limit: 20 } },
            );
            return data.users;
        },
    });

    return {
        users: result.data ?? [],
        isLoading: result.isFetching && trimmed.length > 0,
        errorMessage: result.error
            ? handleError('Search users error', result.error, false)
            : undefined,
    };
}

export function useCmsUser(
    userId: number | null,
    options: { basepath?: string } = {},
) {
    const basepath = getBasepath(options.basepath);

    const result = useQuery({
        queryKey: [USERS_QUERY_KEY, basepath, 'user', userId],
        enabled: userId !== null,
        queryFn: async () => {
            const { data } = await axios.get<CmsUserSummary>(
                `${basepath}/v2/users/${userId}`,
            );
            return data;
        },
    });

    return {
        user: result.data ?? null,
        isLoading: result.isLoading && userId !== null,
        errorMessage: result.error
            ? handleError('Fetch user error', result.error, false)
            : undefined,
    };
}

export function useUpdateCmsUserRoles(options: { basepath?: string } = {}) {
    const basepath = getBasepath(options.basepath);
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: async ({
            userId,
            roles,
        }: {
            userId: number;
            roles: string[];
        }) => {
            const { data } = await axios.put<CmsUserSummary>(
                `${basepath}/v2/users/${userId}/roles`,
                { roles },
            );
            return data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [USERS_QUERY_KEY],
            });
        },
    });

    return {
        saveRoles: mutation.mutateAsync,
        isSaving: mutation.isPending,
        errorMessage: mutation.error
            ? handleError('Update user roles error', mutation.error, false)
            : undefined,
    };
}
